import { useEffect, useRef, type RefObject } from "react";

/**
 * VOD playback heartbeat — ports legacy
 * `js/coaching/feedback-player.js:_startFeedbackHeartbeat`.
 *
 * Why it exists: VOD HLS segments are served directly by Caddy
 * from the bind-mount, so segment fetches never reach FastAPI and
 * never refresh the streams-registry `last_activity`. Without a
 * heartbeat, an active viewer is reaped after ~15s
 * (`HLS_IDLE_SECONDS`) and vanishes from the admin UI mid-
 * playback. This hook POSTs `/api/matches/{id}/heartbeat?slot=`
 * every 10s while the video is playing — the same registry-touch
 * path the playlist fetches take.
 *
 * **Load-bearing for admin "kill" propagation.** When an admin
 * kills a stream the heartbeat returns `403`; the hook then pauses
 * the video, surfaces an error via `onKilled`, and stops pinging.
 * Without the heartbeat there's no channel for the kill to reach
 * a coach reviewing VOD.
 *
 * Behavior, faithful to legacy:
 *   - First ping fires immediately, bypassing the paused/ended
 *     skip-guard (so a session is registered the moment the video
 *     mounts, even before the coach hits play).
 *   - Subsequent pings every 10s; skipped while the video is
 *     paused or ended (no point keeping a session warm for a
 *     video nobody is watching).
 *   - `403` → pause the video + `onKilled()` + stop the loop.
 *   - Transient errors (network blip) are swallowed — the next
 *     tick retries.
 *   - The loop is torn down on unmount, on a match/slot change,
 *     or when `enabled` goes false. A stale in-flight ping after
 *     teardown is ignored via a generation guard.
 */

const HEARTBEAT_INTERVAL_MS = 10_000;

export type UseVodHeartbeatOptions = {
  /** Match id — the heartbeat target. Null/undefined → no heartbeat. */
  matchId: string | null | undefined;
  /** Slot — must be a valid slot (`full` / `first_half` /
   * `second_half`); the backend 400s otherwise. Null → no heartbeat. */
  slot: string | null | undefined;
  /** Ref to the playing `<video>`. Used for the paused/ended
   * skip-guard and to pause on a kill. */
  videoRef: RefObject<HTMLVideoElement | null>;
  /** Called once when the heartbeat gets a `403` — the stream was
   * killed by an admin. The caller surfaces the message to the
   * coach (toast / inline error). */
  onKilled?: () => void;
  /** Master switch — when false the heartbeat doesn't run (e.g.
   * the canvas is showing the no-video placeholder, or focus mode
   * is mounting a second copy and only one should ping). Defaults
   * to true. */
  enabled?: boolean;
};

export function useVodHeartbeat({
  matchId,
  slot,
  videoRef,
  onKilled,
  enabled = true,
}: UseVodHeartbeatOptions): void {
  // Keep the latest `onKilled` in a ref so the effect doesn't
  // re-subscribe (and reset the interval) every render just
  // because the callback identity changed.
  const onKilledRef = useRef(onKilled);
  useEffect(() => {
    onKilledRef.current = onKilled;
  }, [onKilled]);

  useEffect(() => {
    if (!enabled || !matchId || !slot) return;

    // Generation guard: bumped on teardown so an in-flight ping
    // resolving after this effect is torn down can't act on the
    // (now stale) video element.
    let generation = 0;
    const myGeneration = generation;
    let timer: ReturnType<typeof setInterval> | null = null;

    const url = `/api/matches/${encodeURIComponent(matchId)}/heartbeat?slot=${encodeURIComponent(slot)}`;

    const ping = async ({ skipPausedCheck = false } = {}) => {
      const video = videoRef.current;
      // Skip while paused/ended — except the very first ping, which
      // registers the session up front.
      if (!skipPausedCheck && video && (video.paused || video.ended)) return;
      try {
        const resp = await fetch(url, {
          method: "POST",
          credentials: "same-origin",
        });
        if (generation !== myGeneration) return; // torn down mid-flight
        if (resp.status === 403) {
          videoRef.current?.pause();
          onKilledRef.current?.();
          stop();
        }
      } catch {
        // Transient (network blip) — the next tick retries.
      }
    };

    const stop = () => {
      generation++;
      if (timer !== null) {
        clearInterval(timer);
        timer = null;
      }
    };

    // First ping immediately — register the session even before
    // the coach presses play.
    void ping({ skipPausedCheck: true });
    timer = setInterval(() => void ping(), HEARTBEAT_INTERVAL_MS);

    return stop;
  }, [matchId, slot, videoRef, enabled]);
}
